import { engineComponent } from "neo-common";
import { BaseElement } from "./BaseElement";
import { ContainerStyle } from "../Styles/ContainerStyle";
import { StyleManager } from "../../Managers/StyleManager";
import { CanvasManager } from "../../Managers/CanvasManager";
import { HtmlManager } from "../../Managers/HtmlManager";
import { ComponentTypes } from "../../ComponentTypes";

/**
 * ContainerElement class is class which creates holder for other elements.
 * Container element can be used for grouping more display objects and better organization in xmls.
 * Can be defined also by Style with class ContainerStyle.
 * Example 1:
 *   <ContainerElement Class="ContainerElement" X="0" Y="0">
 *       <Child1 />
 *       <Child2 />
 *       ...
 *       <ChildX />
 *   </ContainerElement>
 *
 * Example 2:
 *   <ContainerElement Class="ContainerElement" Style="ContainerStyle" />
 *
 * @class ContainerElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ContainerElement extends BaseElement {
    // style is optional in this element
    style: ContainerStyle;
    // list of children
    childs: Object;

    constructor() {
        super();
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs.hasOwnProperty(key)) {
                    if (this.childs[key] instanceof PIXI.DisplayObject) {
                        this.childs[key].destroy(options);
                    }
                    this.childs[key] = null;
                }
            }

            this.childs = null;
        }
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        super.createByNode(node);

        /*
         * Style can be define for better usage of container elements.
         * For example when you want to use same display objects with same names on more than one places.
         * Style for container element has to be instance of ContainerStyle.
         * @property Style
         * @type string
         * @default null
         */
        if (node.hasAttribute(ContainerElementAttributes.STYLE)) {
            // get style from StyleManager
            this.style = <ContainerStyle>StyleManager.Styles[node.getAttribute(ContainerElementAttributes.STYLE)];
            if (this.style != null) {
                // parse childs from style
                if (this.childs == null) {
                    this.childs = CanvasManager.parseElements(this.style.node, this);
                    HtmlManager.parseElements(this.style.node, null);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name " + node.getAttribute(ContainerElementAttributes.STYLE));
                }
            }
        } else {
            // parse childs from node
            if (this.childs == null) {
                this.childs = CanvasManager.parseElements(node, this);
                HtmlManager.parseElements(node, null);
            }
        }

        if (this.childs == null) {
            if (DEBUG) {
                this.log("WARNING container element with name " + node.nodeName + " has no children");
            }
        }
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ContainerElement
     */
    clone(): ContainerElement {
        let element: ContainerElement = new ContainerElement();

        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.name = this.name;

        element.childs = {};
        element.removeChildren();
        for (let key in this.childs) {
            element.childs[key] = this.childs[key].clone();
            element.addChild(element.childs[key]);
        }

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

const ContainerElementAttributes = {
    DEBUG: "Debug",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    STYLE: "Style",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/ContainerElement.ts
 **/